import { Component, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NgbActiveModal, NgbModalModule } from '@ng-bootstrap/ng-bootstrap';

import { PaymentMethod } from 'src/app/models/common-models';
import { StripeService } from 'src/app/services/stripe.service';

@Component({
  selector: 'df-verify-bank-account',
  templateUrl: './verify-bank-account.component.html',
  styleUrls: ['./verify-bank-account.component.scss'],
  standalone: true,
  imports: [CommonModule, FormsModule, NgbModalModule]
})
export class VerifyBankAccountComponent {
  @Input() paymentMethod!: PaymentMethod;

  submitted = false;
  errorMessage = '';

  // User enters 4 characters (after the SM prefix)
  codeSuffix = '';

  constructor(
    public activeModal: NgbActiveModal,
    private stripeService: StripeService,
  ) {}

  get last4(): string {
    return this.paymentMethod?.us_bank_account?.last4 || '';
  }

  // Best-effort extraction; Stripe may or may not expose this field depending on SDK versions.
  get expectedArrivalDateText(): string {
    const details: any = (this.paymentMethod as any)?.us_bank_account?.status_details;
    const micro = details?.microdeposit || details?.micro_deposit || details?.microDeposits;
    const date = micro?.expected_arrival_date || micro?.expectedArrivalDate;
    if (!date) return '';

    try {
      // Stripe can return unix seconds; handle either
      const d = typeof date === 'number' ? new Date(date * 1000) : new Date(date);
      if (isNaN(d.getTime())) return '';
      return d.toLocaleDateString();
    } catch {
      return '';
    }
  }

  onClose(): void {
    this.activeModal.close(false);
  }

  onVerify(): void {
    this.errorMessage = '';

    const suffix = (this.codeSuffix || '').trim().toUpperCase();
    if (suffix.length !== 4) {
      this.errorMessage = 'Enter the last 4 characters of the verification code.';
      return;
    }

    const pmId = this.paymentMethod?.id;
    if (!pmId) {
      this.errorMessage = 'Missing payment method id.';
      return;
    }

    this.submitted = true;
    this.stripeService.verifyBankAccount(pmId, suffix).subscribe({
      next: () => {
        this.submitted = false;
        this.activeModal.close(true);
      },
      error: (err: any) => {
        this.submitted = false;

        // Angular HttpErrorResponse shape:
        // - err.error can be string (plain text) or object (json)
        // - err.message is generic (e.g. "Http failure response...")
        const raw = err?.error;
        if (typeof raw === 'string' && raw.trim().length > 0) {
          this.errorMessage = raw;
          return;
        }

        const msg = raw?.message || raw?.Message || err?.message;
        this.errorMessage = (typeof msg === 'string' && msg.trim().length > 0)
          ? msg
          : 'Failed to verify bank account.';
      }
    });
  }
}
